import {
  riskSurveyOverviewRoute,
  riskSurveyPlantRoute,
  riskSurveyFeatureTranslations
} from '@features/risk-survey'
import {getPermissionsByUserId, getUserProfile, Permission} from '@hconnect/apiclient'
import {User} from '@hconnect/apiclient/src/response/User'
import {LegalPage} from '@hconnect/common/components/legal/LegalPage'
import {TermsAndConditions} from '@hconnect/common/components/legal/TermsAndConditions'
import {ApiProvider} from '@hconnect/common/hooks/useApi'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {whiteListByPermission, Env} from '@hconnect/common/hproduce'
import {UserContextProvider} from '@hconnect/login'
import {NotificationProvider} from '@hconnect/uikit'
import {Loader} from '@hconnect/uikit/src/lib2'
import {UpmPlantsProvider, useUpmPlantsContext} from '@hconnect/upm'
import {ToastContainer} from '@hm/fundamental'
import {
  FeatureFlagConfig,
  FeatureFlagKey,
  useCommonFeatureFlag,
  useFeatureFlag
} from '@hproduce/feature-flags'
import {useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Route, Routes, Navigate, Link as RouterLink, useNavigate} from 'react-router'

import {ProductRouteHelper} from './entrypointDashboard/components/ProductRoute'
import {getEntrypointDashboardRoutes} from './entrypointDashboard/entrypointDashboard.routes'
import {getHrocRoutes} from './hroc/hroc.routes'
import {useNavAccessibility} from './performanceDashboard/hooks/useNavAccessibility'
import {getPerformanceDashboardRoutes} from './performanceDashboard/performanceDashboard.routes'
import {handleError} from './shared/helpers/errorHandling'
import {TranslationPrefixProvider} from './shared/hooks'
import {ApiContextProvider} from './shared/hooks/useApi'
import {NavAccessibilityContextProvider} from './shared/hooks/useNavAccessContext'
import {DefaultShell} from './shared/layouts'
import {Log} from './shared/lib/Log'
import {initLocalization} from './shared/localization'
import {SimpleErrorPage} from './shared/pages/SimpleErrorPage'
import {ApiConfig} from './shared/provider/createApi'
import {PRIVACY_POLICY, TERMS_AND_CONDITIONS, DOWNTIME, TASKS} from './shared/routes'
import {Context} from './shared/types'
import {AccessStatus} from './shared/types/navigationAccess.types'
import {getTasksRoutes} from './tasks/tasks.routes'

type LoginFlowData = {user: User; userId: string; permissions: Permission[]}

type InitReturnType = LoginFlowData | undefined

const initialize = async (api: ApiConfig): Promise<InitReturnType> => {
  const {loginFlow} = api

  const [loginResponse] = await Promise.all([loginFlow.getLoginState(), initLocalization()])

  if (!loginResponse.loggedIn) {
    await loginFlow.startLoginProcess()
    return
  }

  const userId = loginResponse.decodedToken.user_id

  const [user, permissions] = await Promise.all([
    getUserProfile(api.axiosInstance)(userId),
    getPermissionsByUserId(api.axiosInstance)(userId)
  ])

  return {user, userId, permissions}
}

export const App: React.FC<{api: ApiConfig; loginFlowData: LoginFlowData}> = ({
  api,
  loginFlowData
}) => {
  const {t} = useTranslation()

  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient, {disabledRoutes: [TASKS, DOWNTIME]})

  const navigate = useNavigate()

  const {data, isLoading, error} = useNavAccessibility()

  const upmPlants = useUpmPlantsContext({isInitialLoad: true})
  const areTermsAndConditionsEnabled = useCommonFeatureFlag(FeatureFlagKey.TermsAndConditions)
  const isRiskSurveyEnabled = useFeatureFlag(FeatureFlagConfig.Suite, FeatureFlagKey.RiskSurvey)

  useEffect(() => {
    if (error && !isLoading) {
      handleError(error, navigate)
    }
  }, [error, navigate, isLoading])

  if (isLoading || !upmPlants) {
    return null
  }

  let enablePerformanceDashboard = false
  if (!data && error) {
    enablePerformanceDashboard = false
  } else if (data) {
    const {status} = data
    if (!(status === AccessStatus.NoPermission || status === AccessStatus.NoAccessNoKpi)) {
      enablePerformanceDashboard = true
    }
  }

  const {loginFlow} = api
  const {user, userId, permissions} = loginFlowData

  const whiteList = whiteListByPermission(
    process.env.REACT_APP_STAGE as Env,
    permissions,
    upmPlants
  )

  if (whiteList.missedPlantIds) {
    whiteList.missedPlantIds.forEach((missedPlantId) =>
      Log.extras({userId, missedPlantId}).error('missing config for plantId')
    )
  }

  const context: Context = {
    whiteList,
    onLogout: () => void loginFlow.startLogoutProcess()
  }

  return (
    <NavAccessibilityContextProvider
      initialState={{
        navigationAccess: data ? data : {status: AccessStatus.NoPermission},
        isLoading,
        error
      }}
    >
      <NotificationProvider>
        <Routes>
          {ProductRouteHelper('Maintain', context)}
          {ProductRouteHelper('Planner', context)}
          {ProductRouteHelper('Settings', context)}
          {ProductRouteHelper('Cockpit', context)}
          {ProductRouteHelper('RefraPlanner', context)}

          {getEntrypointDashboardRoutes(context, enablePerformanceDashboard)}

          {getTasksRoutes({context})}

          {getHrocRoutes({context, permissions})}

          {isRiskSurveyEnabled && (
            <>
              <Route
                path="/risk-survey"
                element={
                  <FeatureTranslationsLoader translations={riskSurveyFeatureTranslations}>
                    <TranslationPrefixProvider>
                      <DefaultShell context={context}>
                        {riskSurveyOverviewRoute.element}
                      </DefaultShell>
                    </TranslationPrefixProvider>
                  </FeatureTranslationsLoader>
                }
              />
              <Route
                path="/risk-survey/plant/:upmId"
                element={
                  <FeatureTranslationsLoader translations={riskSurveyFeatureTranslations}>
                    <TranslationPrefixProvider>
                      <DefaultShell context={context}>{riskSurveyPlantRoute.element}</DefaultShell>
                    </TranslationPrefixProvider>
                  </FeatureTranslationsLoader>
                }
              />
            </>
          )}

          {areTermsAndConditionsEnabled && (
            <Route
              path={TERMS_AND_CONDITIONS}
              element={<LegalPage routerLinkComponent={RouterLink} user={user} type="tc" />}
            />
          )}

          {areTermsAndConditionsEnabled && (
            <Route
              path={PRIVACY_POLICY}
              element={<LegalPage routerLinkComponent={RouterLink} user={user} type="dpp" />}
            />
          )}

          {data &&
            enablePerformanceDashboard &&
            getPerformanceDashboardRoutes(context, permissions, t)}
          <Route path={'*'} element={<Navigate to="/" />} />
        </Routes>
      </NotificationProvider>
    </NavAccessibilityContextProvider>
  )
}

const AppContent: React.FC<{api: ApiConfig; loginFlowData: LoginFlowData}> = ({
  api,
  loginFlowData
}) => {
  const areTermsAndConditionsEnabled = useCommonFeatureFlag(FeatureFlagKey.TermsAndConditions)

  return (
    <>
      <ToastContainer />
      <App api={api} loginFlowData={loginFlowData} />
      {areTermsAndConditionsEnabled && <TermsAndConditions user={loginFlowData.user} />}
    </>
  )
}

export const AppWrapper: React.FC<{api: ApiConfig}> = ({api}) => {
  const [data, setData] = useState<InitReturnType>()
  const [error, setError] = useState<AxiosError>()

  useEffect(() => {
    initialize(api)
      .then((d) => setData(d))
      .catch((e) => setError(e as AxiosError))
  }, [api])

  if (!data && error) {
    return (
      <SimpleErrorPage>
        <pre>{(error as Error)?.toString?.()}</pre>
        <pre>{JSON.stringify(error, undefined, 2)}</pre>
      </SimpleErrorPage>
    )
  }

  if (!data) {
    return <Loader data-test-id="initializing-hproduce" />
  }

  return (
    <ApiContextProvider axiosInstance={api.axiosInstance}>
      <ApiProvider secureApi={null} publicApi={api.axiosInstance}>
        <UserContextProvider initialUserValue={data.user}>
          <UpmPlantsProvider api={api.axiosInstance}>
            <AppContent api={api} loginFlowData={data} />
          </UpmPlantsProvider>
        </UserContextProvider>
      </ApiProvider>
    </ApiContextProvider>
  )
}
